import { fetchJSON as fetchAPI } from './client';

export type PlanStatus = 'draft' | 'review' | 'approved' | 'revision' | 'published' | 'frozen' | 'archived';
export type PolicyType = string;
export type RiskType = string;
export type RecommendationType = string;

export type InventoryPlan = {
  id: string;
  tenant_id: string;
  name: string;
  dimension: string;
  node_label?: string;
  status: PlanStatus;
  version: number;
  horizon_days: number;
  target_stock_units: number;
  safety_stock_units: number;
  reorder_point: number;
  max_stock: number;
  current_stock: number;
  days_of_supply: number;
  service_level: number;
  working_capital: number;
  inventory_turnover: number;
  fill_rate: number;
  health_score: number;
  policy_id?: string;
  created_at: string;
  updated_at: string;
};

export type InventoryPolicy = {
  id: string;
  name: string;
  policy_type: PolicyType;
  min_stock: number;
  max_stock: number;
  reorder_point: number;
  order_quantity: number;
  target_days_supply: number;
  service_level_target: number;
  lead_time_days: number;
  enabled: boolean;
  description?: string;
};

export type SafetyStockRecord = {
  id: string;
  plan_id?: string;
  sku?: string;
  safety_stock: number;
  buffer_stock: number;
  emergency_stock: number;
  seasonal_buffer: number;
  promotion_buffer: number;
  lead_time_buffer: number;
  demand_variability_buffer: number;
  service_level_buffer: number;
  calculation_method: string;
  service_level: number;
  explanation: string;
};

export type ReplenishmentPlan = {
  id: string;
  plan_id: string;
  sku?: string;
  quantity: number;
  replenishment_date: string;
  preferred_source: string;
  source_type: string;
  priority: number;
  urgency: string;
  business_justification: string;
  confidence: number;
};

export type InventoryRisk = {
  id: string;
  plan_id?: string;
  sku?: string;
  risk_type: RiskType;
  severity: string;
  confidence: number;
  impact: string;
  recommended_action: string;
  estimated_cost?: number;
  estimated_savings?: number;
};

export type InventoryRecommendation = {
  id: string;
  plan_id?: string;
  type: RecommendationType;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_benefit: string;
  estimated_cost: number;
  estimated_savings: number;
  status: string;
  auto_execute: boolean;
};

export type InventoryScenario = {
  id: string;
  plan_id: string;
  name: string;
  scenario_type: string;
  inventory_units: number;
  working_capital: number;
  revenue: number;
  risk_score: number;
  service_level: number;
  description?: string;
};

export type InventorySegment = {
  id: string;
  sku: string;
  sku_label?: string;
  abc_segment: string;
  xyz_segment: string;
  matrix_cell: string;
  lifecycle_stage: string;
  is_critical: boolean;
  is_strategic: boolean;
  annual_volume: number;
  annual_value: number;
};

export type EchelonNode = {
  id: string;
  plan_id: string;
  level: string;
  location_name: string;
  target_stock: number;
  current_stock: number;
  safety_stock: number;
};

export type InventoryDashboard = {
  tenant_id: string;
  active_plans: number;
  total_skus: number;
  avg_days_of_supply: number;
  avg_service_level: number;
  stockout_pct: number;
  overstock_pct: number;
  safety_stock_utilization: number;
  working_capital: number;
  inventory_turnover: number;
  health_score: number;
  open_risks: number;
  pending_recommendations: number;
  pending_replenishments: number;
};

export type CreateInventoryPlanRequest = {
  name: string;
  dimension?: string;
  node_label?: string;
  horizon_days?: number;
  current_stock?: number;
  daily_demand?: number;
  lead_time_days?: number;
  service_level?: number;
  unit_cost?: number;
};

const API_BASE = import.meta.env.VITE_INVENTORYPLANNING_API ?? '/api/v1';

function invFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const inventoryPlanningApi = {
  dashboard: () => invFetch<InventoryDashboard>('/inventory-dashboard'),
  listPlans: () => invFetch<InventoryPlan[]>('/inventory-plans'),
  getPlan: (id: string) => invFetch<InventoryPlan>(`/inventory-plans/${id}`),
  createPlan: (req: CreateInventoryPlanRequest) =>
    invFetch<InventoryPlan>('/inventory-plans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),
  approvePlan: (id: string) =>
    invFetch<InventoryPlan>(`/inventory-plans/${id}/approve`, { method: 'POST' }),
  listPolicies: () => invFetch<InventoryPolicy[]>('/inventory-policies'),
  listSafetyStock: (planId?: string) =>
    invFetch<SafetyStockRecord[]>(`/safety-stock${planId ? `?plan_id=${planId}` : ''}`),
  listRisks: (planId?: string) =>
    invFetch<InventoryRisk[]>(`/inventory-risks${planId ? `?plan_id=${planId}` : ''}`),
  listRecommendations: (planId?: string) =>
    invFetch<InventoryRecommendation[]>(`/inventory-recommendations${planId ? `?plan_id=${planId}` : ''}`),
  listScenarios: (planId?: string) =>
    invFetch<InventoryScenario[]>(`/inventory-scenarios${planId ? `?plan_id=${planId}` : ''}`),
  listSegments: () => invFetch<InventorySegment[]>('/inventory-segments'),
  listEchelons: (planId?: string) =>
    invFetch<EchelonNode[]>(`/inventory-echelons${planId ? `?plan_id=${planId}` : ''}`),
  listReplenishments: (planId?: string) =>
    invFetch<ReplenishmentPlan[]>(`/replenishment${planId ? `?plan_id=${planId}` : ''}`),
  generateReplenishment: (planId: string) =>
    invFetch<ReplenishmentPlan>('/replenishment/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id: planId }),
    }),
};
